import React, { createContext, useContext, useEffect, useState } from 'react';
import { Alert, Pressable, ScrollView, StyleSheet, Switch, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { C, FONT, R, SP } from '../theme/tokens';
import { useUnitsContext } from '../context/UnitsContext';

// ─── Dark mode context (always-dark app — this is a legacy stub kept for compat) ───
const DARK_MODE_KEY = 'tabai_dark_mode';

interface DarkModeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

const DarkModeContext = createContext<DarkModeContextType>({
  isDarkMode: true,
  toggleDarkMode: () => {},
});

export const useDarkMode = () => useContext(DarkModeContext);

export const DarkModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(true);

  useEffect(() => {
    AsyncStorage.getItem(DARK_MODE_KEY).then((value) => {
      if (value !== null) setIsDarkMode(value === 'true');
    });
  }, []);

  const toggleDarkMode = async () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    await AsyncStorage.setItem(DARK_MODE_KEY, String(next));
  };

  return (
    <DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};

// ─── Settings screen ───────────────────────────────────────────────────────────

function SettingRow({
  label,
  description,
  icon,
  value,
  onToggle,
}: {
  label: string;
  description?: string;
  icon: React.ComponentProps<typeof Ionicons>['name'];
  value: boolean;
  onToggle: () => void;
}) {
  return (
    <View style={row.wrap}>
      <View style={row.iconBox}>
        <Ionicons name={icon} size={18} color={C.ink2} />
      </View>
      <View style={row.body}>
        <Text style={row.label}>{label}</Text>
        {description ? <Text style={row.desc}>{description}</Text> : null}
      </View>
      <Switch
        value={value}
        onValueChange={onToggle}
        trackColor={{ false: C.edge, true: C.accent }}
        thumbColor={C.ink}
        ios_backgroundColor={C.edge}
      />
    </View>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={infoRowStyle.row}>
      <Text style={infoRowStyle.label}>{label}</Text>
      <Text style={infoRowStyle.value}>{value}</Text>
    </View>
  );
}

const SettingsScreen = () => {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { isImperial, setImperial } = useUnitsContext();
  const [autoSave, setAutoSave] = useState(true);
  const [showFormulas, setShowFormulas] = useState(true);

  const handleUnitsChange = () => {
    const next = !isImperial;
    setImperial(next);
    Alert.alert(
      'Units changed',
      `Switched to ${next ? 'Imperial' : 'Metric (SI)'} units.\nAll calculator inputs and results will use ${next ? 'Imperial' : 'SI'} units.`,
      [{ text: 'OK' }]
    );
  };

  return (
    <ScrollView style={s.container} contentContainerStyle={s.content}>
      {/* Header */}
      <View style={s.header}>
        <View style={s.eyebrowRow}>
          <View style={s.eyebrowBar} />
          <Text style={s.eyebrowText}>CONFIGURATION</Text>
        </View>
        <Text style={s.title}>Settings</Text>
        <Text style={s.subtitle}>AeroViz Core preferences</Text>
      </View>

      {/* Preferences */}
      <Text style={s.sectionLabel}>Preferences</Text>
      <View style={s.card}>
        <SettingRow
          label="Units"
          description={isImperial ? 'Imperial  (ft, in, °F, CFM)' : 'Metric SI  (m, mm, °C, L/s)'}
          icon="swap-horizontal-outline"
          value={isImperial}
          onToggle={handleUnitsChange}
        />
        <View style={s.divider} />
        <SettingRow
          label="Auto-save calculations"
          description="Save calculation history automatically"
          icon="save-outline"
          value={autoSave}
          onToggle={() => setAutoSave(!autoSave)}
        />
        <View style={s.divider} />
        <SettingRow
          label="Show formulas"
          description="Display formula card on calculator screens"
          icon="flask-outline"
          value={showFormulas}
          onToggle={() => setShowFormulas(!showFormulas)}
        />
      </View>

      {/* App Info */}
      <Text style={s.sectionLabel}>App</Text>
      <View style={s.card}>
        <InfoRow label="Version" value="0.0.1" />
        <View style={s.divider} />
        <InfoRow label="Build" value="20APR2026" />
        <View style={s.divider} />
        <InfoRow label="Platform" value="Expo / React Native" />
      </View>

      <Text style={s.sectionLabel}>Disclaimer</Text>
      <View style={s.card}>
        <View style={s.disclaimerHeader}>
          <Ionicons name="warning-outline" size={18} color={C.accent} style={{ marginRight: SP.sm }} />
          <Text style={s.disclaimerTitle}>Educational use only</Text>
        </View>
        <Text style={s.disclaimerBody}>
          AeroViz Core is provided for educational purposes only. All results must be independently verified
          by the user against field measurements before being used for any engineering, compliance, or
          safety decision.
        </Text>
        <Text style={s.disclaimerBody}>
          This app is developed independently and is not affiliated with, endorsed by, or associated with
          any certification body, trade organization, or standards-development body.
        </Text>
        <Text style={s.disclaimerBody}>
          All equations are derived from publicly available mathematics, physics, and engineering
          principles. No proprietary formulas or organizational content is included.
        </Text>
        <Text style={s.disclaimerBody}>
          Use at your own risk. The developer disclaims all liability for any decisions, damages, or losses
          arising from use of this application.
        </Text>
      </View>

      <Text style={s.footer}>v0.0.1 · AeroViz Core</Text>
    </ScrollView>
  );
};

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg },
  content: { padding: SP.md, paddingBottom: SP.xl + SP.md, maxWidth: 980, alignSelf: 'center', width: '100%' },

  header: { marginBottom: SP.md },
  eyebrowRow: { flexDirection: 'row', alignItems: 'center', marginBottom: SP.xs },
  eyebrowBar: { width: 4, height: 11, backgroundColor: C.accent, borderRadius: 1, marginRight: SP.sm },
  eyebrowText: { fontFamily: FONT.mono, fontSize: 11, color: C.dim, letterSpacing: 0.8 },
  title: { fontFamily: FONT.sansBold, fontSize: 28, color: C.ink, letterSpacing: -0.4 },
  subtitle: { fontFamily: FONT.sans, fontSize: 14, color: C.ink2, marginTop: SP.xs + 2, lineHeight: 20 },

  sectionLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: SP.sm,
  },

  card: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.md,
    marginBottom: SP.md + 4,
  },
  divider: { height: 1, backgroundColor: C.edge, marginVertical: SP.sm + 2 },

  orgName: { fontFamily: FONT.sansBold, fontSize: 14, color: C.ink, marginBottom: SP.sm },
  contactRow: { flexDirection: 'row', alignItems: 'center', gap: SP.sm, marginBottom: 6 },
  contactText: { fontFamily: FONT.sans, fontSize: 13, color: C.ink2 },
  orgDesc: { fontFamily: FONT.sans, fontSize: 13, color: C.ink2, lineHeight: 19, marginTop: SP.sm },

  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SP.sm + 2,
  },
  actionLabel: { flex: 1, fontFamily: FONT.sans, fontSize: 15, color: C.ink },
  actionChevron: { fontFamily: FONT.mono, fontSize: 20, color: C.dim },
  pressed: { opacity: 0.82 },

  footer: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textAlign: 'center',
    marginTop: SP.md,
    lineHeight: 18,
  },

  disclaimerHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: SP.sm },
  disclaimerTitle: { fontFamily: FONT.sansBold, fontSize: 14, color: C.ink, letterSpacing: 0.2 },
  disclaimerBody: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    lineHeight: 19,
    marginTop: SP.sm,
  },
});

const row = StyleSheet.create({
  wrap: { flexDirection: 'row', alignItems: 'center' },
  iconBox: {
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: C.panel2,
    borderWidth: 1,
    borderColor: C.edge,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: SP.sm + 4,
  },
  body: { flex: 1 },
  label: { fontFamily: FONT.sansMed, fontSize: 15, color: C.ink },
  desc: { fontFamily: FONT.sans, fontSize: 12, color: C.ink2, marginTop: 2 },
});

const infoRowStyle = StyleSheet.create({
  row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6 },
  label: { fontFamily: FONT.sans, fontSize: 14, color: C.ink2 },
  value: { fontFamily: FONT.mono, fontSize: 13, color: C.ink },
});

export default SettingsScreen;
